import { Button, HSpacer, Text, VSpacer } from "@/components/DesignSystem";
import { ModalBottomSheet } from '@/components/shared/ModalBottomSheet/ModalBottomSheet';
import {
  SubwalletProductsBottomSheet,
} from '@/components/shared/SubwalletBalance/SubwalletProductsBottomSheet';
import { QueryKeys } from '@/constants/QueryKeys';
import { SubwalletApi } from '@/utilities/api/SubwalletApi';
import { ResolvedApiSubwalletBalance } from '@api/interfaces';
import { CircularProgress, Divider, Stack, useTheme } from "@mui/material";
import { getClosestExpirationGroup } from "@shared/utilities";
import React, { Fragment, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from "react-material-symbols";
import { useQuery } from 'react-query';

export const SubwalletBalanceModalBottomSheet = ({
  accessToken,
  balances,
  brandColor,
  onClose,
}: {
  accessToken?: string,
  balances: ResolvedApiSubwalletBalance[],
  brandColor?: string,
  onClose: () => void,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [showRedeemableProducts, setShowRedeemableProducts] = useState(false);
  const brandColorBg = brandColor ?? theme.palette.primary.main;

  const expirationGroups = useMemo(() => {
    const map = new Map<string, number>();
    for (const { expirationDate, points } of balances) {
      if (!expirationDate) {
        continue;
      }
      map.set(expirationDate, (map.get(expirationDate) ?? 0) + points);
    }
    return [...map.entries()]
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([expirationDate, points]) => ({ expirationDate, points }))
      .filter(({ points }) => !!points);
  }, [balances]);

  const {
    data: redeemableProducts,
    isFetching: isFetchingRedeemableProducts,
  } = useQuery(
    [QueryKeys.LIST_REDEEMABLE_PRODUCTS, balances[0].subwalletId],
    async () => SubwalletApi.listRedeemableProducts(
      balances[0].subwalletId,
      { accessToken, limit: 1 },
    ),
  );
  const hasRedeemableProducts = (redeemableProducts?.total ?? 0) > 0;

  const renderBalanceItem = (expirationDate: string, points: number) => {
    const days = getClosestExpirationGroup(expirationDate);
    let status: 'error' | 'warning' | 'info' | null = null;
    if (days !== -1) {
      status = 'error';
      if (days >= 30) {
        status = 'warning';
      }
      if (days >= 60) {
        status = 'info';
      }
    }
    return (
      <>
        <VSpacer size="3" />
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Stack alignItems="center" direction="row">
            {status && (
              <>
                <MaterialSymbol
                  color={theme.palette[status].main}
                  fill
                  icon="alarm"
                />
                <HSpacer size="2"/>
              </>
            )}
            <Text category="body-medium">
              {t($ => $.wallet.expiringDate, { date: expirationDate })}
            </Text>
          </Stack>
          <Text category="body-medium">
            {t(
              $ => $.common.units.pointsCount,
              { count: points },
            )}
          </Text>
        </Stack>
        <VSpacer size="3" />
        <Divider/>
      </>
    );
  };

  return (
    <ModalBottomSheet
      acceptButton={(props) => (
        <Button
          {...props}
          onClick={() => onClose()}
          sx={{
            backgroundColor: brandColorBg,
            '&:hover': {
              backgroundColor: brandColorBg,
              opacity: 0.9,
            },
          }}
        >
          {t($ => $.common.actions.close)}
        </Button>
      )}
      footer={(
        <Stack px="16px">
          <Button
            color="inherit"
            onClick={() => onClose()}
            sx={{
              backgroundColor: brandColorBg,
              '&:hover': {
                backgroundColor: brandColorBg,
                opacity: 0.9,
              },
            }}
            testID="footer-close-button"
          >
            {t($ => $.common.actions.close)}
          </Button>
          <VSpacer size="7" />
        </Stack>
      )}
      hideBottomCloseButton
      hideTopCloseButton
      onClose={onClose}
      open
      secondaryActionButton={(props) => (
        isFetchingRedeemableProducts ? (
          <Stack alignItems="center" justifyContent="center" width="190px">
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <Button
            {...props}
            color="primary"
            disabled={!hasRedeemableProducts}
            onClick={() => setShowRedeemableProducts(true)}
            sx={{
              ...props.sx,
              color: brandColorBg,
            }}
          >
            {t($ => $.common.subwallet.redeemableProducts)}
          </Button>
        )
      )}
      showTopButton={false}
      testID="subwallet-balance-modal-bottom-sheet"
      title={t($ => $.common.actions.details)}
      topButtonAction={(
        <Button
          color="primary"
          onClick={() => setShowRedeemableProducts(true)}
          sx={{ color: brandColorBg }}
          testID="reedemable-products-button"
          variant="text"
        >
          {t($ => $.common.subwallet.redeemableProducts)}
        </Button>
      )}
    >
      <Text category="body-large">
        {balances[0].subwallet.redeemableProductsDescription}
      </Text>
      <VSpacer size="7"/>
      <Divider/>
      {expirationGroups.map(({ expirationDate, points }) => (
        <Fragment key={expirationDate}>
          {renderBalanceItem(expirationDate, points)}
        </Fragment>
      ))}
      {showRedeemableProducts && (
        <SubwalletProductsBottomSheet
          accessToken={accessToken}
          brandColor={brandColor}
          onClose={() => setShowRedeemableProducts(false)}
          subwalletId={balances[0].subwalletId!}
        />
      )}
    </ModalBottomSheet>
  );
};
